import { Injectable } from '@angular/core';
import { MsgBoxComponent } from '../shared/msgbox/msgbox.component';
import { NotifyComponent } from '../shared/notify/notify.component';
import { TranslationService } from './translation.service';
import { AiContentGeneratorComponent } from '../components/ai-content-generator/ai-content-generator.component';
import { DataService } from '../data.service';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UtilitiesService {
  public msgBox: MsgBoxComponent;
  public notify: NotifyComponent;
  public aiContentGenerator: AiContentGeneratorComponent;

  constructor(
    public translateService: TranslationService,
    public dataService: DataService,
    private router: Router,
  ) {}

  translate(str, values = []) {
    return this.translateService.translate(str, values);
  }

  getErrorMessageWithRefId(error: any, fallbackMessage: string) {
    const body = error?.error;

    const message = typeof body?.message === 'string' ? body?.message : null;
    const baseMessage =
      message ??
      body?.message?.message ??
      body?.error?.message ??
      body?.message?.error ??
      fallbackMessage;

    const refId =
      body?.refId ??
      body?.ref_id ??
      body?.message?.refId ??
      body?.message?.ref_id;

    const translatedMessage = this.translate(baseMessage);
    if (refId) {
      return `${translatedMessage} \n\nPlease report to eStore Team\n (Ref ID: ${refId})`;
    }

    return translatedMessage;
  }

  async copyToClipboard(str, dataName = 'Data') {
    try {
      await navigator.clipboard.writeText(str);
      this.notify.showInfo(`${dataName} has been copied to the clipboard`);
    } catch (error) {
      console.log('failed to copy to clipboard. error=' + error);
    }
  }

  /**
   * Get system's color scheme preference from prefers-color-scheme
   */
  private getSystemColorScheme(): 'dark' | 'light' {
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return 'dark';
    }
    return 'light';
  }

  /**
   * Initialize dark mode on app startup
   * Priority: localStorage > prefers-color-scheme > default (light)
   */
  initializeDarkMode() {
    const savedMode = localStorage.getItem('dark_mode');

    if (savedMode !== null) {
      // User has manually set a preference - use it
      this.applyDarkMode(savedMode === 'true', false);
    } else {
      // No saved preference - use system preference
      const systemPrefersDark = this.getSystemColorScheme() === 'dark';
      this.applyDarkMode(systemPrefersDark, false);
    }
  }

  toggleDarkMode() {
    const root = document.documentElement;
    const isDark = root.classList.contains('dark');
    if (isDark) {
      this.turnDarkModeOff();
    } else {
      this.turnDarkModeOn();
    }
  }

  turnDarkModeOn() {
    this.setDarkMode(true);
  }

  turnDarkModeOff() {
    this.setDarkMode(false);
  }

  /**
   * Set dark mode and save preference to localStorage
   * This overrides system preference
   */
  setDarkMode(value: boolean) {
    this.applyDarkMode(value, true);
  }

  /**
   * Apply dark mode to DOM and optionally save to localStorage
   * @param value - true for dark mode, false for light mode
   * @param saveToStorage - whether to save preference to localStorage
   */
  private applyDarkMode(value: boolean, saveToStorage: boolean) {
    if (value) {
      if (saveToStorage) {
        localStorage.setItem('dark_mode', 'true');
      }
      document.documentElement.classList.add('dark');
      // Update meta theme-color for mobile browsers
      this.updateThemeColor('#131418'); // dark background color
    } else {
      if (saveToStorage) {
        localStorage.setItem('dark_mode', 'false');
      }
      document.documentElement.classList.remove('dark');
      // Update meta theme-color for mobile browsers
      this.updateThemeColor('#ffffff'); // light background color
    }
  }

  /**
   * Update theme-color meta tag for mobile browsers
   */
  private updateThemeColor(color: string) {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.setAttribute('name', 'theme-color');
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.setAttribute('content', color);
  }

  /**
   * Dashboard theme (Preferences > Profile > Appearance): 'glass' (default)
   * or 'classic'. Glass adds `theme-glass` to <html>, which every glass rule
   * in the stylesheets is scoped to; classic removes it and the dashboard
   * falls back to the original styles. Saved per browser, like dark mode.
   */
  getUiTheme(): 'glass' | 'classic' {
    return localStorage.getItem('ui_theme') === 'classic' ? 'classic' : 'glass';
  }

  setUiTheme(theme: 'glass' | 'classic') {
    localStorage.setItem('ui_theme', theme);
    this.applyUiTheme(theme);
  }

  initializeUiTheme() {
    this.applyUiTheme(this.getUiTheme());
  }

  private applyUiTheme(theme: 'glass' | 'classic') {
    document.documentElement.classList.toggle('theme-glass', theme === 'glass');
  }

  /**
   * Check if currently in dark mode
   */
  isDarkMode(): boolean {
    return document.documentElement.classList.contains('dark');
  }

  /**
   * Get current color scheme (what's actually applied)
   */
  getCurrentColorScheme(): 'dark' | 'light' {
    return this.isDarkMode() ? 'dark' : 'light';
  }

  /**
   * Check if user has manually set a preference
   */
  hasManualPreference(): boolean {
    return localStorage.getItem('dark_mode') !== null;
  }

  switchStore(storeId, storeInfo, showWarning = false, message = '') {
    const currentStoreId = localStorage.getItem('store_id');
    const successMsg =
      message !== '' ? message : 'Switched to "{0}" successfully.';

    if (+currentStoreId === +storeId) {
      if (showWarning) {
        this.notify.showWarning(
          this.translate('"{0}" is already selected.', [
            storeInfo['store_name'],
          ]),
        );
      }
    } else {
      this.dataService.emitLocalStorageData(storeInfo);
      localStorage.setItem('store_id', storeId);
      localStorage.setItem('store_url', storeInfo['store_url']);
      localStorage.setItem('store_name', storeInfo['store_name']);
      this.notify.showSuccess(
        this.translate(successMsg, [storeInfo['store_name']]),
      );
    }
  }

  errorUnauthorized(showMsg = true, msg = null) {
    if (showMsg)
      this.notify.showError(
        this.translate(msg ?? 'Unauthorized user, please login'),
      );

    this.clearSession(true);
    Promise.resolve().then(() => {
      return this.router.navigate(['/login']);
    });
  }

  errorNoPlan(url) {
    this.notify.showError(
      this.translate('Kindly Subscribe to enjoy the benefits of eStore'),
    );
    setTimeout(() => {
      this.clearSession();
      window.location.href = url;
    }, 3000);
  }

  clearSession(fullClear = false) {
    if (fullClear) {
      localStorage.removeItem('5s-info');
    }
    localStorage.removeItem('store_id');
    localStorage.removeItem('store_url');
    localStorage.removeItem('bearer');
    localStorage.removeItem('store_name');
    localStorage.removeItem('default_store');
    localStorage.removeItem('feature');
  }
}
